//
//  AddCustVehicle
//
//  This comp is the Add Vehicle screen used while in Customer Account.
//  It only allows the user to add a vehicle by entering a VIN.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { strings } from 'src/localization';
import { ComponentMapIds } from '../map';
import { TextField, Link, LoadingProgress } from '@framework-controls/core';
import MediaQuery from 'react-responsive';
import { IView } from 'Framework';
import * as css from './AddCustvehicle.module.scss';
import { NewVehicle, VehicleRec, VehicleRecs, DecodeRec, Customer, VehicleStockPhoto, PhotoList, UpdateApptVehInp, ROHistoryData, DepartmentSpec, ascPageviewInput, ascCtaInteractionInput, ascFormEngagementInput, ascElementConfigurationInput } from 'src/models';
import ErrorMessage from 'src/components/ErrorMessage/ErrorMessage';
import Footer from '../Footer/Footer';
import VehicleModelImages from '../VehicleImages/VehicleModelImages';
import { DepartmentContactInfo } from '../RecordLockError/DepartmentContactInfo/DepartmentContactInfo';
import { BlankDeptSpec } from 'src/models/Specs/TestSpecs';
import WhereIsMyVin from '../WhereIsMyVin/WhereIsMyVin';
import { ascCtaInteraction, ascFormEngagement, ascPageview, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';

export interface IProps {
    newVehicleInfo?: NewVehicle;
    custInfo?: Customer;
    vehErr?: string;
    decodeData: DecodeRec;
    mainPaneViewOrder: IView[];
    vehicles: VehicleRecs;
    vehiclePhoto: VehicleStockPhoto;
    vehPhotoList: PhotoList;
    store: string;
    branch: string;
    previewFlag: string;
    roHistory: ROHistoryData;
    profileNum: string;
    theme: string;
    loading: 'idle' | 'pending' | 'succeeded' | 'failed'
    Itype: string | undefined;
    selectedVehicle?: string;
    onAddNewVehicle: (state: NewVehicle) => void;
    decodeVin: (id: string, store: string, branch: string) => void;
    clearDecodeData: () => void;
    popFromMainPane: (id: string) => void;
    pushToMainPanes: (inputView: IView) => void;
    selectVehicle: (id: string) => void;
    addVehicle: (newVehicle: VehicleRec) => void;
    getVehicles: () => void;
    createVehicle: (newVehicle: VehicleRec, custID: string, makeCode: string,
        store: string, branch: string, previewFlag: string, isOEM?: string) => void;
    clearError: () => void;
    updateApptVehicle: (vehicle: UpdateApptVehInp) => void;
    clearROHistory: () => void;
    getROHistory: () => void;
    selectRO: (roNum: string) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    custSearch: (inp: Customer, profileNum: string, store: string, branch: string, searchForSpm: string,
        landingPageFlag?: string) => void;
    specDeptData: DepartmentSpec;
    getDeptSpecs: () => void;
    setLoadingStateToIdle: () => void;
    clearServices: () => void;
    getServices: (profileNum: string, store: string, branch: string) => void;
}

const AddCustVehicle = (props: IProps) => {
    const [localVIN, setLocalVIN] = useState('');
    const [vinError, setVinError] = useState(true);
    const [vinDecoded, setVinDecoded] = useState(false);
    const [decodingVin, setDecodingVin] = useState(false);
    const [addingVin, setAddingVin] = useState(false);
    const [showDecodeError, setShowDecodeError] = useState(false);
    const [showAddError, setShowAddError] = useState(false);
    const [vehImage, setVehImage] = useState(<></>);
    const [showVinExistsError, setShowVinExistsError] = useState(false);
    const [vinIndex, setVinIndex] = useState(-1);
    const [whereOpen, setWhereOpen] = useState(false);
    const [addedVin, setAddedVin] = useState('');
    const [updateVehList, setUpdateVehList] = useState(true);

    const currentViewPanes = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1];

    let linkButtonWide = css.linkButtonWide;
    if (props.theme === 'toyotaSPM') {
        linkButtonWide = css.linkButtonWide + ' ' + css.linkButtonWideSPM;
    }

    useEffect(() => {
        if (props.vehicles.length === 0) {
            props.getVehicles();
        }
    }, [props?.getVehicles, props?.vehicles?.length]);

    useEffect(() => {
        if (props.specDeptData === BlankDeptSpec) {
            props.getDeptSpecs();
        }
    }, [props.specDeptData, props.getDeptSpecs]);

    useEffect(() => {
        if (props.mainPaneViewOrder){
            if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1]?.viewId === ComponentMapIds.AddCustVehicle) {
                const ascPageViewInp: ascPageviewInput = {
                    pageType: 'service',
                    errorCode: '',
                    itemResults: '',
                };
                ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
            }
        }
    }, [props.mainPaneViewOrder, props.vehicles, props.selectedVehicle, whereOpen]);

    //  Add Vehicle
    const AddbtnHandler = React.useCallback((e:any) => {
        const vehicleInfo = { ...props.newVehicleInfo! };
        vehicleInfo.vin = localVIN;
        vehicleInfo.vinValid = true;
        vehicleInfo.makeCode = props.decodeData.makeCode;
        vehicleInfo.make = props.decodeData.make;
        vehicleInfo.model = props.decodeData.model;
        vehicleInfo.year = props.decodeData.year;
        vehicleInfo.trim = props.decodeData.style;
        vehicleInfo.color = props.decodeData.color;
        const newVeh: VehicleRec = {
            id: '',
            vin: localVIN,
            makeCode: vehicleInfo.makeCode,
            make: vehicleInfo.make,
            model: vehicleInfo.model,
            year: vehicleInfo.year,
            color: vehicleInfo.color,
            style: vehicleInfo.trim,
            imagePath: '',
            prevMileage: 0,
            prevRoDate: '',
            nextAppt: '',
            lastAdv: '',
            lastAdvName: '',
            lastAdvImage: '',
            lastTrans: '',
            lastTransLit: '',
            nameId: '',
        };
        props.createVehicle(newVeh, props.custInfo!.nameId, props.decodeData.makeCode,
            props.store, props.branch, props.previewFlag);
        setAddingVin(true);
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.AddVehicle,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'service',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.newVehicleInfo, props.decodeData.makeCode, props.decodeData.make, props.decodeData.model, props.decodeData.year,
        props.decodeData.style, props.decodeData.color, props.createVehicle, props.custInfo, props.store, props.branch,
        props.previewFlag, props.vehicles, props.selectedVehicle, localVIN]);

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Add Customer Veh', '');
    }, [
        props.addLogEntry,
    ]);

    // Decode the VIN
    const decodeBtnHandler = useCallback((event: any) => {
        let vehicleList = props.vehicles.filter(a => a.vin);
        vehicleList = vehicleList.filter(a => a.vin.length === 17);
        const vehicleIndex = vehicleList.findIndex(result => result.vin.substring(9) === localVIN.substring(9));
        if (vehicleIndex !== -1) {
            setVinIndex(vehicleIndex);
            setShowVinExistsError(true);
        } else {
            setDecodingVin(true);
            props.clearDecodeData();
            props.decodeVin(localVIN, props.store, props.branch);
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.DecodeVin,
            elementColor: '',
            elementOrder: '',
            elementType: 'service',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'calc',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, event, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.DecodeVin,
            elementType: 'service',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'calc',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, event);
    }, [localVIN, props.clearDecodeData, props.decodeVin, props.store, props.branch, props.vehicles, props.selectedVehicle]);

    // If the VIN has chnaged set a flag so we display the decode button and not proceed
    const vinChangedHandler = useCallback((_: string, value: string) => {
        const vinValue = value.trim();
        const capValue = vinValue.toUpperCase();
        setLocalVIN(capValue);
        setVinDecoded(false);
        if (capValue.length !== 17) {
            setVinError(true);
        } else {
            setVinError(false);
        }
    }, [setLocalVIN]);

    // Error logic
    useEffect(() => {
        if (decodingVin && props.decodeData.make === 'ERROR') {
            setShowDecodeError(true);
        }
        if (
            localVIN !== '' &&
            decodingVin &&
            props.decodeData.make &&
            props.decodeData.make !== 'ERROR'
        ) {
            setDecodingVin(false);
            setVinDecoded(true);
            // Build Image block
            let makeModel = props.decodeData.model ?
                props.decodeData.make + '  ' + props.decodeData.model : props.decodeData.make;
            if (props.decodeData?.makeCode === '2I') {
                makeModel = props.decodeData.model ? 'Scion  ' + props.decodeData.model : 'Scion';
            }
            let vehInfo = props.decodeData.year;
            if (props.decodeData.color) {
                vehInfo = vehInfo + '  ' + props.decodeData.color;
            }
            if (props.decodeData.style) {
                vehInfo = vehInfo + '  ' + props.decodeData.style;
            }
            const decodeImage = <div className={css.decodeInfo}>
                <div className={css.vehicleImageBlock}>
                    <VehicleModelImages
                        year={props.decodeData.year}
                        make={props.decodeData.make}
                        model={props.decodeData.model}
                        type='full'
                        className={css.vehicleImg}
                        Itype={props.Itype}
                    />
                </div>
                <div className={css.makeModel}>
                    <span>{makeModel}</span>
                </div>
                <div className={css.yearColStyle}>
                    <span>{vehInfo}</span>
                </div>
                <div className={css.enteredInfo}>
                    <div>
                        {localVIN}
                    </div>
                </div>
            </div>;
            setVehImage(decodeImage);
        }
    }, [decodingVin, localVIN, props?.decodeData, props?.Itype]);

    useEffect(() => {
        if (addingVin && props.vehErr === '1002') {
            setAddingVin(false);
            setShowAddError(true);
        }
        if (
            localVIN !== '' &&
            addingVin &&
            props.vehErr === ''
        ) {
            setAddingVin(false);
            const vehicleInfo = { ...props.newVehicleInfo! };
            vehicleInfo.vin = props.decodeData.decodeErr?.split('')[0] === 'VINOWNED' ? '' : localVIN;
            vehicleInfo.vinValid = true;
            vehicleInfo.makeCode = props.decodeData.makeCode;
            vehicleInfo.make = props.decodeData.make;
            vehicleInfo.model = props.decodeData.model;
            vehicleInfo.year = props.decodeData.year;
            vehicleInfo.trim = props.decodeData.style;
            vehicleInfo.color = props.decodeData.color;
            const newVeh: VehicleRec = {
                id: '',
                vin: props.decodeData.decodeErr?.split('')[0] === 'VINOWNED' ? '' : localVIN,
                makeCode: vehicleInfo.makeCode ?? '',
                make: vehicleInfo.make ?? '',
                model: vehicleInfo.model ?? '',
                year: vehicleInfo.year ?? '',
                color: vehicleInfo.color ?? '',
                style: vehicleInfo.trim ?? '',
                imagePath: '',
                prevMileage: 0,
                prevRoDate: '',
                nextAppt: '',
                lastAdv: '',
                lastAdvName: '',
                lastAdvImage: '',
                lastTrans: '',
                lastTransLit: '',
                nameId: '',
            };
            // props.addVehicle(newVeh);
            // const selVehId = (props.vehicles.length) + '';
            setAddedVin(newVeh.vin.slice(-8));
            props.clearError();
            const inp: UpdateApptVehInp = {
                vin: newVeh.vin ?? '',
                makeCode: newVeh.makeCode ?? '',
                make: newVeh.make ?? '',
                model: newVeh.model ?? '',
                series: newVeh.style ?? '',
                vehYr: newVeh.year ?? '',
                vehColor: newVeh.color ?? '',
                mileage: newVeh.prevMileage ?? '',
                imagePath: newVeh.imagePath ?? '',
            };
            props.updateApptVehicle(inp);
            props.clearDecodeData();
            props.clearROHistory();
            props.clearServices();
            props.selectRO('-1');
            // Since the new vehicle has been created in the record, we call custSearch here
            // to update the Customer object, including the Customer vehicle list.
            // Set the landing page flag to 'Y' to get the log record to sync its list of vehicles.
            props.custSearch(props.custInfo!, props.profileNum, props.store, props.branch, 'Y', 'Y');
            setUpdateVehList(false);
        }
    }, [props.vehErr, props.addVehicle, props.selectVehicle, props.clearError, props.vehicles, props.updateApptVehicle,
        props.clearDecodeData, props.pushToMainPanes, props.popFromMainPane, props.getROHistory, props.clearROHistory,
        props.selectRO, props.newVehicleInfo, props.decodeData, addingVin, localVIN, currentViewPanes, props.custInfo,
        props.custInfo?.vehicleList, props.branch, props.custSearch, props.store, props.profileNum, props.clearServices]);

    useEffect(() => {
        if (!updateVehList && props.loading === 'succeeded') {
            props.getVehicles();
            setUpdateVehList(true);
        }
    }, [props.getVehicles, updateVehList, props.loading]);

    useEffect(() => {
        if (props.loading === 'succeeded' && (props.roHistory.built || updateVehList)) {
            const selectedVehicle = props.custInfo?.vehicleList?.find(v => {
                let match = false;
                if (v.vin) {
                    match = v.vin.slice(-8) === addedVin;
                }

                return match;
            });
            if (selectedVehicle) {
                props.selectVehicle(selectedVehicle.id);
            }
            props.popFromMainPane(ComponentMapIds.VehLayout);
            props.popFromMainPane(currentViewPanes.viewId);
            props.pushToMainPanes({
                viewId: ComponentMapIds.VehLayout,
                component: ComponentMapIds.VehLayout,
            });
            props.setLoadingStateToIdle();
            if (selectedVehicle && props.vehicles.length === 0) {
                props.getVehicles();
                props.getROHistory();
                props.selectRO('-1');
            }
        }
    }, [props.popFromMainPane, props.pushToMainPanes, currentViewPanes, props.roHistory, props.selectVehicle,
        props.loading, props.setLoadingStateToIdle, addedVin, props.custInfo, props.getVehicles,
        props.vehicles.length, props.getROHistory, props.selectRO, updateVehList]);
    const decodeLit = strings.DecodeVin;

    const addDecodeBtn = useMemo(() => {
        let tempBtn = <Footer
            id='DecodeVehicle'
            message={decodeLit}
            handler={decodeBtnHandler}
            isDisabled={vinError}
            secondaryIsDisabled
            secondaryhandler={decodeBtnHandler}
        />;
        if (vinDecoded) {
            tempBtn = <Footer
                id='AddVehicle'
                message={strings.AddVehicle}
                handler={AddbtnHandler}
                isDisabled={vinError}
                secondaryIsDisabled
                secondaryhandler={decodeBtnHandler}
            />;
        }

        return tempBtn;
    }, [AddbtnHandler, decodeBtnHandler, vinError, vinDecoded, decodeLit]);

    const closeHandler = useCallback((e: any) => {
        setShowDecodeError(false);
        setShowAddError(false);
        setShowVinExistsError(false);
        props.clearError();
        props.clearDecodeData();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.TryAgain,
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        props.clearServices();
        props.getROHistory();
        props.selectRO('-1');
        props.getServices(props.profileNum, props.store, props.branch);
    }, [props.clearError, props.clearDecodeData, props.vehicles, props.selectedVehicle, props.clearServices,
        props.getROHistory, props.selectRO, props.getServices, props.profileNum, props.store, props.branch]);

    const loadVehicleHandler = useCallback((e: any) => {
        setShowVinExistsError(false);
        const selVehId = (vinIndex) + '';
        props.selectVehicle(selVehId);
        props.clearError();
        const vehicle = props.vehicles.find(veh => veh.id === selVehId);
        const inp: UpdateApptVehInp = {
            vin: vehicle!.vin,
            makeCode: vehicle!.makeCode,
            make: vehicle!.make,
            model: vehicle!.model,
            series: vehicle!.style,
            vehYr: vehicle!.year,
            vehColor: vehicle!.color,
            mileage: vehicle!.prevMileage,
            imagePath: vehicle!.imagePath,
        };
        props.updateApptVehicle(inp);
        props.clearDecodeData();
        props.popFromMainPane(currentViewPanes.viewId);
        props.pushToMainPanes({
            viewId: ComponentMapIds.VehLayout,
            component: ComponentMapIds.VehLayout,
        });
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Yes,
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [currentViewPanes.viewId, props.clearDecodeData, props.popFromMainPane, props.clearError, props.pushToMainPanes,
        props.selectVehicle, props.updateApptVehicle, props.vehicles, props.selectedVehicle, vinIndex]);

    const whereHandler = useCallback(() => {
        setWhereOpen(!whereOpen);
    }, [whereOpen]);

    const interactionHandler = useCallback((e: any) => {
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.WhereIsMyVIN,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'information_link',
            eventAction: 'click',
            eventActionResult: whereOpen ? 'close' : 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.WhereIsMyVIN,
            elementType: 'popup',
            elementColor: elementColor,
            eventAction: 'click',
            eventActionResult: whereOpen ? 'close' : 'open',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [props.selectedVehicle, props.vehicles, whereOpen]);

    const decodeError = useMemo(() => {
        if (showDecodeError) {
            setDecodingVin(false);
            setShowDecodeError(true);

            return GetDecodeErrorMessage(closeHandler);
        }

        return null;
    }, [showDecodeError, closeHandler]);

    const addError = useMemo(() => {
        if (showAddError) {
            setAddingVin(false);
            setShowAddError(true);

            return GetAddErrorMessage(closeHandler, props.specDeptData.contactPhnNum, props.specDeptData.contactExt);
        }

        return null;
    }, [showAddError, closeHandler, props.specDeptData.contactPhnNum, props.specDeptData.contactExt]);

    const vinExistsError = useMemo(() => {
        if (showVinExistsError) {
            return GetVinExistsErrorMessage(closeHandler, loadVehicleHandler);
        }

        return null;
    }, [showVinExistsError, closeHandler, loadVehicleHandler]);

    const keyFunction = useCallback((e) => {
        if (e.charCode === 13) {
            if (vinDecoded) {
                AddbtnHandler(e);
            } else {
                decodeBtnHandler(e);
            }
        }
    }, [decodeBtnHandler, AddbtnHandler, vinDecoded]);

    const handleVinBlur = useCallback(() => {
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.VIN + '*',
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp);
    }, []);

    // IconNoImage is placeholder. Will probably be replaced with imagePath or something
    return (
        <div>
            <div className={css.addCustVeh}>
                <h2>{strings.AddVehicle}</h2>
                <h3>{strings.AddCustVehMsg}</h3>
                {decodeError}
                {addError}
                {vinExistsError}
                {decodingVin || addingVin ? <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
                <hr />
                {vehImage}
                {vinDecoded ? <hr /> : null}
                <span>{strings.RequiredFields}</span>
                <div className={css.inputRow}>
                    <div className={css.inputFields}>
                        <MediaQuery maxWidth={1200}>
                            <Link className={css.linkButtonThin} onClick={whereHandler}>
                                <div onClick={(e: any) => interactionHandler(e)}>
                                    {strings.WhereIsMyVIN}
                                </div>
                            </Link>
                        </MediaQuery>
                        <TextField
                            id='vinTextField'
                            label={strings.VIN + '*'}
                            value={localVIN ? localVIN : ''}
                            type='text'
                            onChange={vinChangedHandler}
                            size='full'
                            variant='VIN'
                            useAutocomplete={false}
                            onKeyPress={(e) => keyFunction(e)}
                            onBlur={handleVinBlur}
                        />
                        <MediaQuery minWidth={1201}>
                            <Link className={linkButtonWide} onClick={whereHandler}>
                                <div onClick={(e: any) => interactionHandler(e)}>{strings.WhereIsMyVIN}</div>
                            </Link>
                        </MediaQuery>
                        <WhereIsMyVin
                            whereOpen = {whereOpen}
                            whereHandler={(e: any) => {
                                whereHandler();
                                interactionHandler(e);
                            }}
                        />
                    </div>
                </div>
            </div>
            {addDecodeBtn}
        </div>
    );
};

function GetDecodeErrorMessage(closeHandler: (event: any) => void) {
    const headerMsg = strings.VinDecodeError;
    const errorMsg = strings.VinDecodeMsg;
    const primaryMessage = strings.TryAgain;

    return (
        <ErrorMessage
            headerMsg={headerMsg}
            errorMsg={errorMsg}
            primaryBtnMessage={primaryMessage}
            secondaryBtnMessage=''
            primaryHandler={closeHandler}
            secondaryHandler={closeHandler}
            closeHandler={closeHandler}
        />
    );
}

function GetAddErrorMessage(closeHandler: (e: any) => void, contactNumber?: string, ext?: string) {
    const headerMsg = strings.VinCreateError;
    let contactInfo: JSX.Element | null = null;
    // Build custom contact information JSX
    if (contactNumber) {
        contactInfo = (
            <span>
                {strings.IssuContLit}
                <DepartmentContactInfo contactPhone={contactNumber} extension={ext}/>
            </span>
        );
    }
    // Build custom error JSX
    const customError = (
        <div>
            {strings.VehicleAddError}
            {contactInfo}
        </div>
    );
    const primaryMessage = strings.TryAgain;

    return (
        <ErrorMessage
            headerMsg={headerMsg}
            primaryBtnMessage={primaryMessage}
            secondaryBtnMessage=''
            primaryHandler={closeHandler}
            secondaryHandler={closeHandler}
            closeHandler={closeHandler}
            customError={customError}
        />
    );
}

function GetVinExistsErrorMessage(closeHandler: (e: any) => void, loadVehicleHandler: (e: any) => void) {
    const headerMsg = strings.VinCreateError;
    const errorMsg = strings.VinExistsMsg;
    const primaryMessage = strings.Yes;
    const secondaryMessage = strings.Cancel;

    return (
        <ErrorMessage
            headerMsg={headerMsg}
            errorMsg={errorMsg}
            primaryBtnMessage={primaryMessage}
            secondaryBtnMessage={secondaryMessage}
            primaryHandler={loadVehicleHandler}
            secondaryHandler={closeHandler}
            closeHandler={closeHandler}
        />
    );
}

export default AddCustVehicle;